import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { WAREHOUSE_DEAL_VIEW } from '../../../../router/routes';
import { route } from '../../../../services/utils';
import { useService } from '../../../../services/services';
import Table from '../../../../components/common/table/table';
import css from '../index.module.css';
import Deal from '../../../../interfaces/deal';
import { ListParams } from '../../../../services/http-client';
import DealType from '../../../../enums/deal-type';
import DateFilter, {
  DateFields,
} from '../../../../components/common/table/date-filter/date-filter';
import ValueFilter from '../../../../components/common/table/value-filter/value-filter';
import ShowFilter from '../../../../components/common/table/show-filter/show-filter';
import ExcelButtonNew from '../../../../components/common/table/excel-button/excel-button-new';
import ShowIfAllowed from '../../../../components/common/show-if-allowed';
import CreateButtons from '../components/create-buttons';
import { useWarehouseContext } from './warehouse-context';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '../../../../hooks/use-query';

export default function Deals() {
  const { t } = useTranslation();
  const { dealService } = useService();
  const { farmId } = useParams<{ farmId: string }>();
  const [params] = useQuery();
  const [showFilter, setShowFilter] = useState(true);
  const [types, setTypes] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [documentNumbers, setDocumentNumbers] = useState([]);
  const { closeModalAddPartner } = useWarehouseContext();

  useEffect(() => {
    closeModalAddPartner();
  }, [closeModalAddPartner]);

  useEffect(() => {
    const $usedDealType = dealService
      .usedDealTypes(farmId, params)
      .subscribe((types) => {
        setTypes(
          types.map((type) => {
            let label;

            if (type.id === DealType.lost) {
              label =
                t(`classifiers.${DealType.lost}`) +
                '/' +
                t(`classifiers.${DealType.leftover}`).toLowerCase();
            } else {
              label = t(`classifiers.${type.id}`);
            }

            return {
              label: label,
              value: type.id,
            };
          })
        );
      });

    const $categories = dealService
      .usedProductCategories(farmId, params)
      .subscribe((categories) => {
        setProductCategories(
          categories.map((category) => ({
            label: category.value,
            value: category.id,
          }))
        );
      });

    const $productTypes = dealService
      .usedProductTypes(farmId, params)
      .subscribe((types) => {
        setProductTypes(
          types.map((type) => ({ label: type.value, value: type.value }))
        );
      });

    const $products = dealService
      .usedProducts(farmId, params)
      .subscribe((products) => {
        setProducts(
          products.map((product) => ({
            label: product.name,
            value: product.name,
          }))
        );
      });

    const $partners = dealService
      .usedPartners(farmId, params)
      .subscribe((partners) => {
        setPartners(
          partners.map((partner) => ({
            label: partner.name,
            value: partner.id,
          }))
        );
      });

    const $documentNumbers = dealService
      .usedDocumentNumbers(farmId, params)
      .subscribe((documentNumbers) => {
        setDocumentNumbers(
          documentNumbers.map((documentNumber) => ({
            label: documentNumber.label,
            value: documentNumber.value,
          }))
        );
      });

    return () => {
      $usedDealType.unsubscribe();
      $categories.unsubscribe();
      $productTypes.unsubscribe();
      $products.unsubscribe();
      $partners.unsubscribe();
      $documentNumbers.unsubscribe();
    };
  }, [dealService, farmId, t, params]);

  const request = useCallback(
    (
      page: number,
      sort: string,
      desc: boolean,
      filter: { [key: string]: any }
    ) => dealService.index(farmId, { page, sort, desc, ...filter }),
    [dealService, farmId]
  );

  const viewRoute = useCallback(
    (deal: Deal) =>
      route(WAREHOUSE_DEAL_VIEW, {
        farmId,
        dealType: deal.deal.deal_type.id,
        id: deal.deal_id,
      }),
    [farmId]
  );

  const toolbar = useCallback(
    (params: ListParams): JSX.Element => {
      return (
        <>
          <div className={css['movement-topbar']}>
            <div>
              <ShowIfAllowed ability={{ title: 'create deal' }}>
                <CreateButtons farmId={farmId} />
              </ShowIfAllowed>
            </div>
          </div>
          <div className={css['show_filter_div']}>
            <div className={css['filter-show']}>
              <label
                htmlFor="show-filter"
                className={css['filter-label']}
              ></label>
              <ShowFilter
                onClick={() => setShowFilter(!showFilter)}
                defaultActiveValue={true}
              />
            </div>

            <div className={css['excel-export']}>
              <label
                htmlFor="excel-export"
                className={css['filter-label']}
              ></label>
              <ExcelButtonNew
                onClick={() => {
                  return dealService
                    .exportXls(farmId, { ...params.params })
                    .subscribe();
                }}
              />
            </div>
          </div>
          {showFilter && (
            <div className={css['filter_body_div']}>
              <div className={css['filter_div']}>
                <label htmlFor="date_from">
                  {t('pages.warehouse.deals.date_from')}
                </label>
                <DateFilter
                  name={'date_from'}
                  className={'date-filter-width-full'}
                  fields={[DateFields.from]}
                  showDateControls={false}
                  placeholder="-"
                  isClearable={false}
                  customIcon={faChevronDown}
                />
              </div>
              <div className={css['filter_div']}>
                <label htmlFor="date_to">
                  {t('pages.warehouse.deals.date_to')}
                </label>
                <DateFilter
                  name={'date_to'}
                  className={'date-filter-width-full'}
                  fields={[DateFields.to]}
                  showDateControls={false}
                  placeholder="-"
                  isClearable={false}
                  customIcon={faChevronDown}
                />
              </div>
              <div className={css['filter_div']}>
                <label>{t('pages.warehouse.deals.deal')}</label>
                <ValueFilter
                  name="type"
                  className={css['value_filter']}
                  options={types}
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                />
              </div>

              <div className={css['filter_div']}>
                <label>{t('pages.warehouse.deals.product_category')}</label>
                <ValueFilter
                  name="product_category"
                  className={css['value_filter']}
                  options={productCategories}
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                />
              </div>
              <div className={css['filter_div']}>
                <label>{t('pages.warehouse.deals.product_type')}</label>
                <ValueFilter
                  options={productTypes}
                  className={css['value_filter']}
                  name="product_type_value"
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                />
              </div>
              <div className={css['filter_div']}>
                <label>{t('pages.warehouse.deals.name')}</label>
                <ValueFilter
                  options={products}
                  className={css['value_filter']}
                  name="product_name"
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                />
              </div>
              <div className={css['filter_div']}>
                <label>{t('pages.warehouse.deals.partner')}</label>
                <ValueFilter
                  options={partners}
                  className={css['value_filter']}
                  name="partner"
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                />
              </div>
              <div className={css['filter_div']}>
                <label>{t('pages.warehouse.deals.document_number')}</label>
                <ValueFilter
                  options={documentNumbers}
                  className={css['value_filter']}
                  name="purchase_document_number"
                  placeholder="-"
                  customIcon={faChevronDown}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                />
              </div>
            </div>
          )}
        </>
      );
    },
    [
      farmId,
      dealService,
      types,
      productCategories,
      productTypes,
      products,
      partners,
      documentNumbers,
      t,
      showFilter,
    ]
  );

  return (
    <div className={css['body']}>
      <Table<Deal>
        toolbar={toolbar}
        request={request}
        keyAccessor={'id'}
        columns={[
          {
            accessor: 'date',
            header: t('pages.warehouse.deals.date'),
            sortable: true,
            className: css['strong'],
          },
          {
            accessor: 'deal.deal_type.name',
            header: t('pages.warehouse.deals.deal'),
            sortable: true,
            className: css['strong'],
            valuable: (value, deal: Deal) => {
              if (deal.deal.deal_type.id === DealType.recycled) {
                return deal.product_obtain_type?.name;
              } else {
                return value;
              }
            },
          },
          {
            accessor: 'product_category.value',
            header: t('pages.warehouse.deals.product_category'),
            sortable: true,
            className: css['strong'],
          },
          {
            accessor: 'product_type.value',
            header: t('pages.warehouse.deals.product_type'),
          },
          {
            accessor: 'product.name',
            header: t('pages.warehouse.deals.name'),
          },
          {
            accessor: 'amount',
            header: t('pages.warehouse.deals.amount'),
            sortable: true,
            valuable: (value, deal: Deal) => {
              return `${value} ${deal.unit}`;
            },
          },
          {
            accessor: 'sum',
            header: t('pages.warehouse.deals.sum'),
            sortable: true,
          },
          {
            accessor: 'deal.partner.name',
            header: t('pages.warehouse.deals.partner'),
            sortable: true,
          },
          {
            accessor: 'deal.purchase_document_number',
            header: t('pages.warehouse.deals.document_number'),
            sortable: true,
          },
          {
            accessor: 'warehouse_names',
            header: t('pages.warehouse.deals.warehouse'),
            sortable: true,
          },
        ]}
        viewRoute={viewRoute}
        viewRecordAbility={{ title: 'view deal' }}
      />
    </div>
  );
}
